<script lang="ts" setup>
import { Form, FormField, type FormInstance } from '@primevue/forms';
import { useRouter } from 'vue-router';
import { PasswordStrength } from '~/components/auth/labels/';
import { AppButton, AppPassword, AppTextInput } from '~/components/base';
import useRegister from '~/composables/auth/useRegister';
import RegisterForm from '~/utils/contents/auth/forms/RegisterForm';

const formRef = useTemplateRef<FormInstance>('formRef');
const router = useRouter();
const route = useRoute();
const { loading, form, errors, validations, resolver, register } = useRegister({
  initialForm: {
    username: route.query.email ? String(route.query.email) : undefined,
    invite_code: route.query.invite_code
      ? String(route.query.invite_code)
      : undefined,
  },
});

const isFormInvalid = computed((): boolean => {
  return !validations.safeParse(form.value).success;
});

const showPasswordStrength = computed((): boolean => {
  return !!form.value.password || !!form.value.password_confirmation;
});

const onFormSubmit = async ({ valid }: { valid: boolean }): Promise<void> => {
  if (!valid) return;

  const result = await register();
  if (result) {
    router.push({ name: 'auth-verify' });
  }
};

const hasInviteCode = computed((): boolean => {
  return !!form.value.invite_code;
});

onMounted(() => {
  if (hasInviteCode.value) {
    formRef.value?.setFieldValue('username', form.value.username || '');
  }
});
</script>

<template>
  <Form
    ref="formRef"
    :resolver="resolver"
    data-testid="register-form"
    class="register-form flex w-full flex-col"
    @submit="onFormSubmit"
  >
    <FormField v-slot="$field" name="username" class="mb-3">
      <AppTextInput
        ref="username"
        v-model="form.username"
        required
        :label="RegisterForm.fields.email.label"
        :placeholder="RegisterForm.fields.email.placeholder"
        :data-testid="RegisterForm.fields.email.dataTestId"
        :disabled="loading || hasInviteCode"
        :invalid="$field?.invalid || !!errors.email?.length"
        :error-message="$field.error?.message || errors.email?.[0]"
        autocomplete="email"
        class="w-full"
      />
    </FormField>
    <FormField v-slot="$field" name="password" class="mb-3">
      <AppPassword
        v-model="form.password"
        required
        :feedback="false"
        :label="RegisterForm.fields.password.label"
        :placeholder="RegisterForm.fields.password.placeholder"
        :data-testid="RegisterForm.fields.password.dataTestId"
        :disabled="loading"
        :invalid="$field?.invalid || !!errors.password?.length"
        :error-message="$field.error?.message || errors.password?.[0]"
        class="w-full"
        autocomplete="new-password"
      />
    </FormField>
    <FormField v-slot="$field" name="password_confirmation" class="mb-5">
      <AppPassword
        v-model="form.password_confirmation"
        required
        :feedback="false"
        :label="RegisterForm.fields.password_confirmation.label"
        :placeholder="RegisterForm.fields.password_confirmation.placeholder"
        :data-testid="RegisterForm.fields.password_confirmation.dataTestId"
        :disabled="loading"
        :invalid="$field?.invalid || !!errors.password_confirmation?.length"
        :error-message="
          $field.error?.message || errors.password_confirmation?.[0]
        "
        class="w-full"
        autocomplete="new-password"
      />
    </FormField>

    <PasswordStrength
      v-if="showPasswordStrength"
      :password="form.password"
      class="mb-5"
      data-testid="password-strength"
    />
    <AppButton
      type="submit"
      :data-testid="RegisterForm.actions.submitButton.dataTestId"
      :disabled="loading || isFormInvalid"
      :loading="loading"
      :label="RegisterForm.actions.submitButton.label"
    />
  </Form>
</template>

<style></style>
